import { splitStatePrefix, US_STATES, type USStateCode } from "./us-state-code";

/**
 * The canonical Miter tax jurisdiction code: `fed` (federal) or a lowercase US state / territory code
 * (e.g. `ca`, `pr`). This is the one internal representation - convert every other form (Check ids,
 * clean names, uppercase state abbreviations) to a `JurisdictionCode` at the boundary and pass this
 * around thereafter.
 */
export type JurisdictionCode = "fed" | Lowercase<USStateCode>;

export const FEDERAL_JURISDICTION = "fed";
export const FEDERAL_NAME = "Federal";

/** Federal plus every US state / territory, in that order. The canonical list to iterate. */
export const ALL_JURISDICTIONS: { jurisdiction: JurisdictionCode; name: string; isFederal: boolean }[] = [
  { jurisdiction: FEDERAL_JURISDICTION, name: FEDERAL_NAME, isFederal: true },
  ...US_STATES.map((state) => ({
    jurisdiction: state.abbreviation.toLowerCase() as JurisdictionCode,
    name: state.name,
    isFederal: false,
  })),
];

const STATE_CODES: Set<string> = new Set(US_STATES.map((state) => state.abbreviation.toLowerCase()));
const NAME_BY_CODE: Map<JurisdictionCode, string> = new Map([
  [FEDERAL_JURISDICTION, FEDERAL_NAME] as [JurisdictionCode, string],
  ...US_STATES.map((state) => [state.abbreviation.toLowerCase(), state.name] as [JurisdictionCode, string]),
]);
const STATE_ABBREVIATION_BY_CODE: Map<JurisdictionCode, USStateCode> = new Map(
  US_STATES.map((state) => [state.abbreviation.toLowerCase() as JurisdictionCode, state.abbreviation])
);

// oxlint-disable-next-line local/restricted-syntax
export const isFederalJurisdiction = (code: string): boolean =>
  code.trim().toLowerCase() === FEDERAL_JURISDICTION;

/** "CA" | "ca" → "ca"; undefined for anything that isn't a US state (never yields "fed"). */
// oxlint-disable-next-line local/restricted-syntax
export const stateAbbreviationToJurisdictionCode = (
  abbreviation?: string | null
): JurisdictionCode | undefined => {
  if (!abbreviation) return undefined;
  const code = abbreviation.trim().toLowerCase();
  return STATE_CODES.has(code) ? (code as JurisdictionCode) : undefined;
};

/** "ca" → "CA"; undefined for "fed" (federal has no state abbreviation). */
// oxlint-disable-next-line local/restricted-syntax
export const jurisdictionCodeToStateAbbreviation = (code: JurisdictionCode): USStateCode | undefined =>
  STATE_ABBREVIATION_BY_CODE.get(code);

/**
 * "California" | "California SDI" | "Federal" → code; undefined for generic labels that carry no
 * jurisdiction ("Have you elected ..."). Accepts both clean names and name-prefixed labels.
 */
// oxlint-disable-next-line local/restricted-syntax
export const jurisdictionNameToCode = (name: string): JurisdictionCode | undefined => {
  const normalized = name.trim().toLowerCase();
  if (normalized === "federal" || normalized.startsWith("federal ")) return FEDERAL_JURISDICTION;
  const abbreviation = splitStatePrefix(name)?.state.abbreviation;
  return abbreviation ? (abbreviation.toLowerCase() as JurisdictionCode) : undefined;
};

/** "ca" → "California"; "fed" → "Federal"; undefined for unknown codes. */
// oxlint-disable-next-line local/restricted-syntax
export const jurisdictionCodeToName = (code: JurisdictionCode): string | undefined => NAME_BY_CODE.get(code);
